import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';

// Format date helper function using native JavaScript
const formatDate = (dateString, formatType = 'long') => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (formatType === 'long') {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } catch (e) {
    return 'Invalid Date';
  }
};

const ReportDetailPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMedicalAdmin = user && (user.role === 'medical_admin' || (user.role && user.role.toLowerCase().includes('medical_admin')));
  const isAdminOrProgram = user && (
    (typeof user.role === 'string' && (user.role === 'medical_admin' || user.role === 'program_admin')) ||
    (user.role && user.role.toLowerCase && (user.role.toLowerCase().includes('medical_admin') || user.role.toLowerCase().includes('program_admin')))
  );
  const [report, setReport] = useState(null);
  const [doctorProgress, setDoctorProgress] = useState(null);
  const [doctorProgressError, setDoctorProgressError] = useState(null);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [totalAssignedAllDoctors, setTotalAssignedAllDoctors] = useState(0);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
  const [showProtocolProgress, setShowProtocolProgress] = useState(true);

  // Load doctors who have assignments created by this medical admin
  useEffect(() => {
    const loadAssignedDoctors = async () => {
      if (!user) return;
      try {
        let url = '/assignments/details';
        const role = typeof user.role === 'string' ? user.role.toLowerCase() : (user.role?.name || '').toLowerCase();
        if (role === 'medical_admin' || role.includes('medical_admin')) {
          url = `/assignments/details?assigned_by=${user.id}`;
        }
        const resp = await api.get(url);
        // response shape may vary: try common possibilities
        const assignments = resp.data?.assignments || resp.data || [];
        // Build unique list of doctors
        const doctorsMap = {};
        let totalAssigned = 0;
        assignments.forEach(a => {
          const docId = a.doctor_id || a.doctorId || a.doctor || null;
          const docName = a.doctor_name || a.doctor_name || (a.doctor && a.doctor.name) || null;
          if (docId) {
            if (!doctorsMap[docId]) doctorsMap[docId] = { id: docId, name: docName || `Dr ${docId}` };
          }
          // Sum assigned duration if available
          const dur = a.assigned_duration_seconds || a.assignedDurationSeconds || 0;
          totalAssigned += Number(dur || 0);
        });

        const doctorsList = Object.values(doctorsMap);
        setAssignedDoctors(doctorsList);
        setTotalAssignedAllDoctors(totalAssigned);

        if (doctorsList.length > 0) {
          // default to first doctor and load progress
          setSelectedDoctorId(doctorsList[0].id);
          loadDoctorProgress(doctorsList[0].id);
        } else {
          // If no doctor-specific assignments exist, clear progress
          setDoctorProgress(null);
        }
      } catch (err) {
        console.error('Failed to load assigned doctors', err);
      }
    };

    loadAssignedDoctors();
  }, [user]);

  useEffect(() => {
    loadReportDetails();
    if (user && user.role === 'medical_admin') {
      // Optionally load a default doctor or wait for selection. For now, do nothing until doctor is selected.
    }
  }, [reportId]);

  useEffect(() => {
    if (report) {
      console.log('Report data:', {
        id: report.id,
        title: report.title,
        type: report.report_type,
        hasContent: Boolean(report.content),
        contentLength: report.content?.length,
        hasMetrics: Boolean(report.metrics?.length),
        hasVisualizations: Boolean(report.visualizations?.length),
        hasTables: Boolean(report.tables?.length),
        hasConclusion: Boolean(report.conclusion)
      });
    }
  }, [report]);

  const loadReportDetails = async () => {
    setLoading(true);
    try {
      console.log(`Fetching report ${reportId}`);
      const response = await api.get(`/reports/${reportId}`);
      console.log('Report details response:', response.data);
      
      if (response.data && response.data.success) {
        const reportData = response.data.report;
        // Add default values for possibly missing fields
        const processedReport = {
          ...reportData,
          content: reportData.content || "No detailed analysis available.",
          conclusion: reportData.conclusion || "No conclusion provided.",
          metrics: reportData.metrics || [],
          visualizations: reportData.visualizations || [],
          tables: reportData.tables || []
        };
        
        console.log("Processed report data:", processedReport);
        setReport(processedReport);
      } else {
        setError('Failed to load report details');
      }
    } catch (err) {
      console.error('Error loading report details:', err);
      setError(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch doctor progress for a given doctor id (medical admin only)
  const loadDoctorProgress = async (doctorId) => {
    if (!doctorId) return;
    try {
      setDoctorProgressError(null);
      const resp = await api.get(`/reports/doctor-progress?doctor_id=${doctorId}`);
      console.log('[ReportDetailPage] doctor-progress response:', resp.data);
      if (resp.data && resp.data.success) {
        setDoctorProgress(resp.data);
      } else {
        setDoctorProgress(null);
        setDoctorProgressError(resp.data?.error || 'No progress data');
      }
    } catch (err) {
      console.error('[ReportDetailPage] Failed to load doctor progress', err);
      setDoctorProgress(null);
      setDoctorProgressError(err.response?.data?.message || err.message || 'Request failed');
    }
  };

  return (
    <ResearchLayout pageTitle="Report Details">
      <div className="container-fluid">
        {/* Back Button */}
        <div className="row mb-4">
          <div className="col">
            <button 
              className="btn btn-outline-secondary" 
              onClick={() => navigate('/medical-admin/reports')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Reports
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : report ? (
          <>
            {/* Report Header */}
            <div className="row mb-4">
              <div className="col">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span className={`badge ${
                          report.report_type === 'statistical' ? 'bg-primary' : 
                          report.report_type === 'research' ? 'bg-success' : 
                          report.report_type === 'clinical' ? 'bg-warning' : 
                          'bg-info'
                        } mb-2`}>
                          {report.report_type || 'Report'}
                        </span>
                        <h2 className="mb-1">{report.title || 'Untitled Report'}</h2>
                        <p className="text-muted mb-0">
                          Created by {report.created_by_name || 'Statistician'} on {formatDate(report.created_at, 'long')}
                        </p>
                      </div>
                      <button className="btn btn-outline-primary">
                        <i className="fas fa-download me-2"></i>
                        Download Report
                      </button>
                    </div>
                    <hr />
                    <p className="mb-0">{report.description}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Report Content */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h4 className="mb-4">Analysis & Findings</h4>
                    
                    {report.content ? (
                      <div className="report-content mb-4">
                        <div dangerouslySetInnerHTML={{ __html: report.content }} />
                      </div>
                    ) : report.healthMetrics ? (
                      <div className="mb-4">
                        <h5>Health Metrics</h5>
                        <div className="row">
                          <div className="col-md-3 mb-3">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h2 className="mb-0">{Math.round(report.healthMetrics.averageScore || 0)}</h2>
                                <p className="mb-0 text-muted">Average Score</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h2 className="mb-0">{Math.round(report.healthMetrics.medianScore || 0)}</h2>
                                <p className="mb-0 text-muted">Median Score</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h2 className="mb-0">{report.healthMetrics.totalAssessments || 0}</h2>
                                <p className="mb-0 text-muted">Total Assessments</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card bg-light">
                              <div className="card-body text-center">
                                <h2 className="mb-0">{report.healthMetrics.stdDeviation?.toFixed(1) || 0}</h2>
                                <p className="mb-0 text-muted">Standard Deviation</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        No detailed analysis is available for this report.
                      </div>
                    )}
                    
                    {/* Display demographic breakdown if available */}
                    {report.demographicBreakdown && Object.keys(report.demographicBreakdown).length > 0 && (
                      <div className="mt-4">
                        <h5 className="mb-3">Demographic Breakdown</h5>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>Demographic Group</th>
                                <th>Count</th>
                                <th>Average Score</th>
                                <th>% of Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(report.demographicBreakdown).map(([group, data], index) => (
                                <tr key={index}>
                                  <td>{group}</td>
                                  <td>{data.count}</td>
                                  <td>{data.averageScore?.toFixed(1) || 'N/A'}</td>
                                  <td>{data.percentage?.toFixed(1)}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Key Metrics */}
                    {report.metrics && report.metrics.length > 0 && (
                      <div className="row mb-4">
                        {report.metrics.map((metric, index) => (
                          <div key={index} className="col-md-3 mb-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body text-center">
                                <h3 className="text-primary">{metric.value}</h3>
                                <p className="text-muted mb-0">{metric.label}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Doctor Progress (visible to medical admins) */}
                     {user && user.role === 'medical_admin' && (
                       <div className="mt-4">
                         <h5 className="mb-3">Doctor Progress</h5>
                        <div className="row mb-3">
                          <div className="col-md-4">
                            {assignedDoctors.length > 0 ? (
                              <>
                                <select
                                  className="form-select"
                                  value={selectedDoctorId || ''}
                                  onChange={e => {
                                    const id = parseInt(e.target.value, 10);
                                    setSelectedDoctorId(id);
                                    loadDoctorProgress(id);
                                  }}
                                >
                                  {assignedDoctors.map(d => (
                                    <option key={d.id} value={d.id}>{d.name || `Dr ${d.id}`}</option>
                                  ))}
                                </select>
                                <small className="text-muted">Select a doctor to view progress</small>
                              </>
                            ) : (
                              <div>
                                <div className="mb-2"><strong>Total assigned time:</strong> {Math.round((totalAssignedAllDoctors || 0) / 60)} min</div>
                                <div className="alert alert-secondary">No doctors assigned yet.</div>
                              </div>
                            )}
                          </div>
                        </div>

                         {doctorProgress ? (
                           <div className="card border-0 shadow-sm">
                             <div className="card-body">
                               <div className="d-flex align-items-center mb-3">
                                 <div className="me-3"><i className="fas fa-user-md fa-2x text-primary"></i></div>
                                 <div>
                                   <h6 className="mb-0">{doctorProgress.doctor_name || doctorProgress.doctor_id}</h6>
                                   <small className="text-muted">Doctor</small>
                                 </div>
                               </div>

                               <div className="row">
                                 <div className="col-md-3">
                                   <div className="card bg-light mb-3">
                                     <div className="card-body text-center">
                                       <h5 className="mb-0">{Math.round((doctorProgress.total_assigned_duration_seconds || 0) / 60)} min</h5>
                                       <p className="mb-0 text-muted">Total Assigned Time</p>
                                     </div>
                                   </div>
                                 </div>
                                 <div className="col-md-3">
                                   <div className="card bg-light mb-3">
                                     <div className="card-body text-center">
                                       <h5 className="mb-0">{Math.round((doctorProgress.elapsed_seconds_estimate || 0) / 60)} min</h5>
                                       <p className="mb-0 text-muted">Estimated Time Used</p>
                                     </div>
                                   </div>
                                 </div>
                                 <div className="col-md-6 d-flex flex-column justify-content-center">
                                   {(() => {
                                     if (doctorProgress.protocols && doctorProgress.protocols.length > 0) {
                                       const totalAssigned = doctorProgress.protocols.reduce((s, p) => s + (p.assigned_seconds || 0), 0);
                                       const weighted = totalAssigned > 0 ? Math.round(doctorProgress.protocols.reduce((acc, p) => acc + ((p.assigned_seconds || 0) * (p.percent_complete || 0)), 0) / totalAssigned) : 0;
                                       return (
                                         <>
                                           <div className="mb-2 d-flex justify-content-between align-items-center">
                                             <div className="small text-muted">Progress</div>
                                             <div className="small text-muted">{weighted}%</div>
                                           </div>
                                           <div className="progress" role="progressbar" aria-label="Doctor progress" aria-valuenow={weighted} aria-valuemin="0" aria-valuemax="100">
                                             <div className="progress-bar bg-warning" style={{ width: `${weighted}%` }} />
                                           </div>
                                         </>
                                       );
                                     }

                                     return (
                                       <>
                                         <div className="mb-2 d-flex justify-content-between align-items-center">
                                           <div className="small text-muted">Progress</div>
                                           <div className="small text-muted">{doctorProgress.percent_complete_estimate || 0}%</div>
                                         </div>
                                         <div className="progress" role="progressbar" aria-label="Doctor progress" aria-valuenow={doctorProgress.percent_complete_estimate || 0} aria-valuemin="0" aria-valuemax="100">
                                           <div className="progress-bar bg-warning" style={{ width: `${doctorProgress.percent_complete_estimate || 0}%` }} />
                                         </div>
                                       </>
                                     );
                                   })()}
                                 </div>
                               </div>

                               {/* Per-protocol smaller progress bars */}
                               {doctorProgress.assignments && doctorProgress.assignments.length > 0 && (
                                 (() => {
                                   const map = {};
                                   doctorProgress.assignments.forEach(a => {
                                     const key = a.protocol_id || `p-${a.protocol_name || 'unknown'}`;
                                     if (!map[key]) map[key] = { protocol_id: a.protocol_id, protocol_name: a.protocol_name || `Protocol ${a.protocol_id}`, assigned_seconds: 0, answered_questions: 0 };
                                     map[key].assigned_seconds += (a.assigned_duration_seconds || 0);
                                     map[key].answered_questions += (a.answered_question_count || 0);
                                   });
                                   const protocols = Object.values(map);
                                   return (
                                     <div className="mb-3">
                                       <div className="d-flex justify-content-between align-items-center mb-2">
                                         <div className="small text-muted">Protocol progress</div>
                                         <button
                                           type="button"
                                           className="btn btn-sm btn-link text-decoration-none"
                                           onClick={() => setShowProtocolProgress(s => !s)}
                                           aria-expanded={showProtocolProgress}
                                         >
                                           {showProtocolProgress ? (<><i className="fas fa-chevron-up"></i> Hide</>) : (<><i className="fas fa-chevron-down"></i> Show</>)}
                                         </button>
                                       </div>
                                       <div className={`collapse ${showProtocolProgress ? 'show' : ''}`}>
                                         <div className="d-flex flex-column gap-2">
                                         {protocols.map(p => {
                                           const elapsed = (p.answered_questions || 0) * 2 * 60;
                                           const pct = p.assigned_seconds > 0 ? Math.min(100, Math.round((elapsed / p.assigned_seconds) * 100)) : 0;
                                           return (
                                             <div key={p.protocol_id} className="d-flex align-items-center">
                                               <div className="me-2" style={{width: '160px'}}>
                                                 <small className="fw-semibold">{p.protocol_name}</small>
                                               </div>
                                               <div className="flex-grow-1">
                                                 <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100" style={{height: '10px'}}>
                                                   <div className="progress-bar bg-info" style={{width: `${pct}%`}} />
                                                 </div>
                                               </div>
                                               <div className="ms-3" style={{width: '50px'}}>
                                                 <small className="text-muted">{pct}%</small>
                                               </div>
                                             </div>
                                           );
                                         })}
                                         </div>
                                       </div>
                                     </div>
                                   );
                                 })()
                               )}

                               <h6 className="mt-3">Assignments</h6>
                               <div className="table-responsive">
                                 <table className="table table-sm">
                                   <thead className="table-light">
                                     <tr>
                                       <th>Assignment ID</th>
                                       <th>Case ID</th>
                                       <th>Protocol</th>
                                       <th>Assigned Qs</th>
                                       <th>Assigned Time (min)</th>
                                       <th>Answered Qs</th>
                                       <th>Status</th>
                                     </tr>
                                   </thead>
                                   <tbody>
                                     {doctorProgress.assignments && doctorProgress.assignments.map((a) => (
                                       <tr key={a.assignment_id}>
                                         <td>{a.assignment_id}</td>
                                         <td>{a.case_id}</td>
                                         <td>{a.protocol_name || a.protocol_id}</td>
                                         <td>{a.assigned_question_count || 0}</td>
                                         <td>{Math.round((a.assigned_duration_seconds || 0) / 60)}</td>
                                         <td>{a.answered_question_count || 0}</td>
                                         <td>{a.status}</td>
                                       </tr>
                                     ))}
                                   </tbody>
                                 </table>
                               </div>
                             </div>
                           </div>
                         ) : (
                           <div className="card border-0 shadow-sm">
                             <div className="card-body text-center py-5">
                               <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                               <h5>No reports found</h5>
                               <p className="mb-0 text-muted">No progress reports exist for the selected doctor. Assign protocols or wait for the doctor to begin answering questions.</p>
                             </div>
                           </div>
                         )}
                       </div>
                     )}

                    {/* Visualizations */}
                    {report.visualizations && report.visualizations.length > 0 && (
                      <div className="mb-4">
                        <h5 className="mb-3">Data Visualizations</h5>
                        <div className="row">
                          {report.visualizations.map((viz, index) => (
                            <div key={index} className="col-md-6 mb-4">
                              <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                  <h6 className="card-title">{viz.title}</h6>
                                  <img 
                                    src={viz.image_url} 
                                    alt={viz.title} 
                                    className="img-fluid rounded" 
                                  />
                                  {viz.description && (
                                    <p className="text-muted mt-2 mb-0">
                                      {viz.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Data Tables */}
                    {report.tables && report.tables.length > 0 && (
                      <div className="mb-4">
                        <h5 className="mb-3">Data Tables</h5>
                        {report.tables.map((table, index) => (
                          <div key={index} className="mb-4">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body">
                                <h6 className="mb-3">{table.title}</h6>
                                <div className="table-responsive">
                                  <table className="table table-bordered table-hover">
                                    <thead className="table-light">
                                      <tr>
                                        {table.headers.map((header, i) => (
                                          <th key={i}>{header}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {table.data.map((row, rowIdx) => (
                                        <tr key={rowIdx}>
                                          {row.map((cell, cellIdx) => (
                                            <td key={cellIdx}>{cell}</td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Report Conclusions */}
            {report.conclusion && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h4 className="mb-3">Conclusion</h4>
                      <p>{report.conclusion}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="alert alert-warning" role="alert">
            Report not found
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default ReportDetailPage;